import React, { useEffect, useMemo, useState } from "react";

import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import InputAdornment from "@material-ui/core/InputAdornment";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

import toastError from "../../errors/toastError";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
	content: {
		minWidth: 420,
		[theme.breakpoints.down("xs")]: {
			minWidth: "auto",
		},
	},
	description: {
		marginBottom: theme.spacing(2),
		color: theme.palette.text.secondary,
	},
	formGrid: {
		display: "grid",
		gridTemplateColumns: "1fr 1fr",
		gap: theme.spacing(2),
		marginBottom: theme.spacing(2),
		[theme.breakpoints.down("xs")]: {
			gridTemplateColumns: "1fr",
		},
	},
	categoryGrid: {
		display: "grid",
		gridTemplateColumns: "1.4fr 1fr 1fr 1fr",
		gap: theme.spacing(2),
		alignItems: "center",
		marginBottom: theme.spacing(1.5),
		[theme.breakpoints.down("xs")]: {
			gridTemplateColumns: "1fr",
			paddingBottom: theme.spacing(1.5),
			borderBottom: `1px solid ${theme.palette.divider}`,
		},
	},
	categoryTitle: {
		fontWeight: 600,
	},
	resultBox: {
		padding: theme.spacing(2),
		borderRadius: 8,
		backgroundColor: theme.palette.grey[100],
	},
	resultValue: {
		fontWeight: 700,
		marginTop: theme.spacing(0.5),
	},
	loading: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		marginBottom: theme.spacing(2),
	},
	externalButton: {
		marginTop: theme.spacing(2),
	},
}));

const brlCurrencyFormatter = new Intl.NumberFormat("pt-BR", {
	style: "currency",
	currency: "BRL",
});

const usdCurrencyFormatter = new Intl.NumberFormat("pt-BR", {
	style: "currency",
	currency: "USD",
});

const numberFormatter = new Intl.NumberFormat("pt-BR");

const initialCategories = [
	{
		key: "marketing",
		label: "Marketing",
		description: "Promoções, ofertas e campanhas.",
		quantity: "",
		cost: "0,0625",
	},
	{
		key: "utility",
		label: "Utilidade",
		description: "Confirmações, avisos e atualizações transacionais.",
		quantity: "",
		cost: "0,0068",
	},
];

const parseNumber = (value) => {
	const normalizedValue = String(value || "")
		.replace(/\./g, "")
		.replace(",", ".");

	const parsedValue = Number(normalizedValue);
	return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const WabaCalculatorDialog = ({ open, onClose }) => {
	const classes = useStyles();
	const [loading, setLoading] = useState(false);
	const [month, setMonth] = useState("");
	const [categories, setCategories] = useState(initialCategories);
	const [exchangeRate, setExchangeRate] = useState("5,20");
	const [lastMonth, setLastMonth] = useState([]);

	useEffect(() => {
		const fetchCalculatorData = async () => {
			if (!open) return;

			setLoading(true);
			try {
				const { data } = await api.get("/tickets/calculator/last_month");
				setLastMonth(data);
			} catch (err) {
				toastError(err);
			} finally {
				setLoading(false);
			}
		};

		fetchCalculatorData();
	}, [open]);

	useEffect(() => {
		const fetchCalculatorData = async () => {
			if (!open) return;

			setLoading(true);
			try {
				const { data } = await api.get("/tickets/calculator/templates/last_30_days");
				setCategories((currentCategories) =>
					currentCategories.map((category) => {
						const categoryTotal = data.find((item) => {
							return item.categoria === category.key.toUpperCase();
						});

						return {
							...category,
							quantity: String(categoryTotal?.total || 0),
						};
					})
				);
				setMonth("ultimos 30 dias");
			} catch (err) {
				toastError(err);
				setCategories(initialCategories);
				setMonth("");
			} finally {
				setLoading(false);
			}
		};

		fetchCalculatorData();
	}, [open]);

	const totals = useMemo(() => {
		const totalTemplates = categories.reduce((total, category) => {
			return total + parseNumber(category.quantity);
		}, 0);

		const totalUsd = categories.reduce((total, category) => {
			return total + parseNumber(category.quantity) * parseNumber(category.cost);
		}, 0);

		return {
			totalTemplates,
			totalUsd,
			totalBrl: totalUsd * parseNumber(exchangeRate),
		};
	}, [categories, exchangeRate]);

	const handleChangePositiveNumber = (setter) => (event) => {
		const { value } = event.target;

		if (/^\d*([,.]\d{0,4})?$/.test(value)) {
			setter(value);
		}
	};

	const handleOpenExternalCalculator = () => {
		window.open(
			`https://calculadora.whatscontabil.com.br?opened=${lastMonth.opened || ""}&month=${lastMonth.month || ""}`,
			"_blank"
		);
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Estimativa de custo WABA</DialogTitle>
			<DialogContent dividers className={classes.content}>
				<Typography className={classes.description}>
					Custos estimados para o <b>mês atual</b>. <br/>Ajuste o valor de conversão do dolar para obter uma estimativa de custo mensal do WABA mais precisa.
				</Typography>

				{loading && (
					<div className={classes.loading}>
						<CircularProgress size={18} />
						<Typography variant="body2">Buscando envios do mes...</Typography>
					</div>
				)}

				<div className={classes.formGrid}>
					<TextField
						label="Cotação estimada do dolar"
						value={exchangeRate}
						onChange={handleChangePositiveNumber(setExchangeRate)}
						variant="outlined"
						size="small"
						disabled={loading}
						fullWidth
						InputProps={{
							startAdornment: <InputAdornment position="start">R$</InputAdornment>,
						}}
					/>
				</div>

				{categories.map((category) => (
					<div className={classes.categoryGrid} key={category.key}>
						<div>
							<Typography className={classes.categoryTitle}>{category.label}</Typography>
							<Typography variant="caption" color="textSecondary">
								{category.description}
							</Typography>
						</div>
						<TextField
							label="Templates"
							value={category.quantity}
							variant="outlined"
							size="small"
							disabled
							fullWidth
						/>
						<TextField
							label="Custo"
							value={category.cost}
							variant="outlined"
							size="small"
							disabled
							fullWidth
							InputProps={{
								startAdornment: <InputAdornment position="start">US$</InputAdornment>,
							}}
						/>
						<TextField
							label="Total"
							value={usdCurrencyFormatter.format(parseNumber(category.quantity) * parseNumber(category.cost))}
							variant="outlined"
							size="small"
							disabled
							fullWidth
						/>
					</div>
				))}

				<div className={classes.resultBox}>
					<Typography variant="body2" color="textSecondary">
						Estimativa mensal{month ? ` para ${month}` : ""}
					</Typography>
					<Typography variant="h5" className={classes.resultValue}>
						{brlCurrencyFormatter.format(totals.totalBrl)}
					</Typography>
					<Typography variant="caption" color="textSecondary">
						{numberFormatter.format(totals.totalTemplates)} templates enviados
					</Typography>
					<Typography variant="body2" className={classes.resultValue}>
						Total em dolar: {usdCurrencyFormatter.format(totals.totalUsd)}
					</Typography>
				</div>

				<Button
					className={classes.externalButton}
					onClick={handleOpenExternalCalculator}
					color="primary"
					variant="outlined"
					fullWidth
				>
					Abrir calculadora
				</Button>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} color="secondary" variant="outlined" disabled={loading}>
					Fechar
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default WabaCalculatorDialog;
